// Where each platform keeps files waiting to upload, so sending carries on after the app restarts.
// The web and phones use IndexedDB, one database per account. The desktop app keeps them in its
// data folder, which every account on the computer shares, so each account's files carry its own
// prefix and one account never sees, or clears away, another's.

import { type ByteSource, type FileSpool, IdbFileSpool } from "@rhema/client";

export const uploadDbName = (userId: string) => `rhema-uploads-${userId}`;

/** IndexedDB for this account, or nothing where there is no IndexedDB, and files stay in memory. */
export function idbUploadSpool(userId: string): FileSpool | undefined {
  return typeof indexedDB === "undefined" ? undefined : new IdbFileSpool(uploadDbName(userId));
}

/** A short prefix from the account id. Spool ids allow letters, digits and dashes, up to 64. */
export function spoolPrefix(userId: string): string {
  return `${userId.replace(/[^A-Za-z0-9]/g, "").slice(0, 16)}-`;
}

/** Keeps one account's files apart inside a spool shared by several accounts. */
export class PrefixedSpool implements FileSpool {
  constructor(
    private readonly inner: FileSpool,
    private readonly prefix: string,
  ) {}

  put(id: string, file: Blob): Promise<boolean> {
    return this.inner.put(this.prefix + id, file);
  }

  open(id: string): Promise<ByteSource | null> {
    return this.inner.open(this.prefix + id);
  }

  delete(id: string): Promise<void> {
    return this.inner.delete(this.prefix + id);
  }

  async ids(): Promise<string[]> {
    return (await this.inner.ids())
      .filter((id) => id.startsWith(this.prefix))
      .map((id) => id.slice(this.prefix.length));
  }
}
